import * as Popover from '@radix-ui/react-popover'
import { type NodeViewProps } from '@tiptap/core'
import { NodeViewWrapper } from '@tiptap/react'
import _ from 'lodash'
import { Asterisk, ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { SwitchField } from '#app/components/forms.js'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '#app/components/ui/select.js'
import { Textarea } from '#app/components/ui/textarea.js'

const connectedFields = {
	email: 'Email',
	firstName: 'First name',
	lastName: 'Last name',
}

type FieldTypes = 'firstName' | 'lastName' | 'custom'

export const Component = (props: NodeViewProps) => {
	const editable = props.editor.isEditable
	const required = props.node.attrs.required
	const [inputType, setInputType] = useState('custom')

	const handleInputName = (name: string) => {
		if (inputType === 'custom') {
			props.updateAttributes({ name: `data_${_.camelCase(name)}` })
		}
		props.updateAttributes({ placeholder: name })
	}

	const handleConnectInput = (value: FieldTypes) => {
		if (value === 'custom') {
			setInputType(value)
		} else {
			props.updateAttributes({ name: value })
			props.updateAttributes({ placeholder: connectedFields[value] })
			setInputType('connected')
		}
	}

	const handleOptionsChange = (value: string) => {
		props.updateAttributes({ options: value.split('\n').filter(Boolean) })
	}

	if (editable) {
		return (
			<NodeViewWrapper className="flex flex-col gap-2">
				<Popover.Root>
					<Popover.Trigger>
						<div className="relative mb-4">
							{required && (
								<div className="absolute -right-1.5 -top-2">
									<Asterisk width={16} className="input-tt-text" />
								</div>
							)}

							<div className="absolute inset-y-1/4 right-3">
								<ChevronDown width={16} className="input-tt-text" />
							</div>

							<input
								type="text"
								onChange={e => handleInputName(e.currentTarget.value)}
								defaultValue={props.node.attrs.placeholder}
								value={props.node.attrs.placeholder}
								className="input-tt w-full appearance-none"
								placeholder="Field name"
							/>
						</div>
					</Popover.Trigger>
					<Popover.Portal>
						<Popover.Content
							onOpenAutoFocus={e => e.preventDefault()}
							className="custom-popover z-50 flex items-center gap-2 rounded-xl border border-border bg-white p-2 shadow-sm"
							sideOffset={8}
						>
							<div className="flex flex-col items-center gap-2">
								<div className="flex items-center gap-2">
									<SwitchField
										labelProps={{ children: 'Required' }}
										defaultChecked={props.node.attrs.required}
										onChange={checked =>
											props.updateAttributes({ required: checked })
										}
									/>
									<div className="relative w-32">
										<Select
											value={props.node.attrs.name || 'custom'}
											onValueChange={value =>
												handleConnectInput(value as FieldTypes)
											}
										>
											<SelectTrigger className="h-[32px]">
												<SelectValue placeholder="Connected field" />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="custom">Custom</SelectItem>
												{Object.entries(connectedFields).map(
													([value, name]) => (
														<SelectItem key={value} value={value}>
															{name}
														</SelectItem>
													),
												)}
											</SelectContent>
										</Select>
									</div>
								</div>
								<div className="flex flex-col gap-2">
									<label className="text-sm text-gray-600">
										Enter options (one per line):
									</label>
									<Textarea
										className="h-48 rounded-lg border border-border p-2"
										defaultValue={
											props.node.attrs.options
												? props.node.attrs.options.join('\n')
												: ''
										}
										onChange={e => handleOptionsChange(e.target.value)}
										placeholder={`Option 1\nOption 2\nOption 3`}
									/>
								</div>
							</div>
						</Popover.Content>
					</Popover.Portal>
				</Popover.Root>
			</NodeViewWrapper>
		)
	} else {
		return (
			<NodeViewWrapper>
				<div className="relative mb-4">
					{required && (
						<div className="absolute inset-y-1/4 right-3">
							<Asterisk width={16} className="input-tt-text" />
						</div>
					)}

					<Select name={props.node.attrs.name} required={required}>
						<SelectTrigger className="input-tt w-full">
							<SelectValue
								placeholder={`Select ${props.node.attrs.placeholder}...`}
							/>
						</SelectTrigger>
						<SelectContent>
							<SelectItem key="select-option" value="select-option">
								Select an option...
							</SelectItem>
							{props.node.attrs.options &&
								(props.node.attrs.options || []).map((option: string) => (
									<SelectItem key={option} value={option}>
										{option}
									</SelectItem>
								))}
						</SelectContent>
					</Select>
				</div>
			</NodeViewWrapper>
		)
	}
}

export default Component
